import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import Modal from "@/components/Modal";
import TraitSelectionModal from "@/components/TraitSelectionModal";
import { StatBars } from "@/components/StatBars";
import { CLASS_META, CLASS_IMAGE } from "@/lib/game";
import { Shield, PenLine, ChevronRight, Hammer } from "lucide-react";
import BattleReplay from "@/components/BattleReplay";

// ─── Step 1: Pick Your Starter Gladiator ────────────────────────────────────

function PickGladiatorStep({ candidates, onPicked }) {
  const [selected, setSelected] = useState(null);
  const [editName, setEditName] = useState("");
  const [busy, setBusy] = useState(false);

  const pick = (c) => {
    setSelected(c);
    setEditName(c.name);
  };

  const confirm = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const { data } = await api.post("/onboarding/pick-gladiator", {
        starter_id: selected.id,
        name: editName.trim() || selected.name,
      });
      onPicked(data);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open title="Choose Your Starting Gladiator" wide>
      <p className="text-sm text-zinc-400 mb-5">
        Every Lanista begins with a single fighter. Select a class to get started.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
        {candidates.map((c) => {
          const meta = CLASS_META[c.cls] || {};
          const Icon = meta.icon || Shield;
          const isSelected = selected?.id === c.id;
          return (
            <button
              key={c.id}
              data-testid={`starter-${c.cls.toLowerCase()}`}
              onClick={() => pick(c)}
              className={`text-left border rounded-sm p-4 transition-all ${
                isSelected
                  ? "border-amber-500 bg-amber-950/30 shadow-[0_0_16px_rgba(217,119,6,0.2)]"
                  : "border-zinc-800 bg-zinc-900/40 hover:border-zinc-600"
              }`}
            >
              <div className="w-full aspect-[3/4] overflow-hidden rounded-sm mb-3 bg-zinc-800">
                <img src={CLASS_IMAGE[c.cls]} alt={c.cls} className="w-full h-full object-cover object-top" />
              </div>
              <div className="flex items-center gap-2 mb-2">
                <Icon size={18} className="text-amber-400" />
                <span className="font-cinzel text-sm uppercase tracking-wide text-zinc-100">{c.cls}</span>
              </div>
              <p className="text-[11px] text-zinc-500 mb-3 leading-relaxed">{c.class_info?.description || ""}</p>
              <StatBars skills={c.skills} compact />
            </button>
          );
        })}
      </div>

      {/* Name editor */}
      {selected && (
        <div className="border border-zinc-800 rounded-sm p-4 mb-4 bg-zinc-900/30">
          <div className="flex items-center gap-2 mb-2">
            <PenLine size={14} className="text-amber-400" />
            <span className="text-[11px] uppercase tracking-widest text-zinc-500">Name Your Gladiator</span>
          </div>
          <input
            data-testid="starter-name-input"
            type="text"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            maxLength={24}
            className="w-full bg-zinc-950 border border-zinc-700 px-3 py-2 text-sm text-zinc-100 focus:border-amber-500 outline-none rounded-sm"
          />
        </div>
      )}

      <div className="flex justify-end">
        <button
          data-testid="starter-confirm"
          onClick={confirm}
          disabled={!selected || busy}
          className="px-6 py-2 text-sm font-cinzel uppercase tracking-widest bg-amber-500 text-zinc-950 hover:bg-amber-400 disabled:opacity-40 transition-colors rounded-sm flex items-center gap-2"
        >
          {busy ? "Recruiting..." : "Recruit"} <ChevronRight size={14} />
        </button>
      </div>
    </Modal>
  );
}


// ─── Step 2: Forge Equipment Prompt ─────────────────────────────────────────

function ForgePromptStep({ gladiator, onForged }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [hasSeen, setHasSeen] = useState(false);

  // If not on equipment page, redirect there
  useEffect(() => {
    if (!location.pathname.startsWith("/equipment")) {
      navigate("/equipment?tab=blacksmith");
    } else {
      setHasSeen(true);
    }
  }, [location.pathname, navigate]);

  // Listen for forge completion
  useEffect(() => {
    const checkForge = async () => {
      try {
        const { data } = await api.get("/onboarding");
        if (data.step !== "forge_equipment") {
          onForged();
        }
      } catch {}
    };

    // Poll every 3s to detect when user has forged
    const id = setInterval(checkForge, 3000);
    return () => clearInterval(id);
  }, [onForged]);

  if (!hasSeen) return null;

  return (
    <div data-testid="forge-prompt" className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 max-w-md w-full mx-4">
      <div className="border border-amber-600/50 bg-zinc-950/95 backdrop-blur-sm rounded-sm p-4 shadow-[0_0_24px_rgba(217,119,6,0.15)]">
        <div className="flex items-start gap-3">
          <Hammer size={20} className="text-amber-400 shrink-0 mt-0.5" />
          <div>
            <h4 className="font-cinzel text-sm uppercase tracking-wide text-amber-300 mb-1">Forge Your First Weapon</h4>
            <p className="text-xs text-zinc-400 leading-relaxed">
              Claim your free fragments above, then forge a weapon for <span className="text-zinc-200 font-medium">{gladiator?.name || "your gladiator"}</span>.
              Your first weapon must match your starter&apos;s class — after that, you can forge freely.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}


// ─── Step 2b: Equip Gear Prompt ──────────────────────────────────────────────

function EquipGearStep({ gladiator, onEquipped }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [hasSeen, setHasSeen] = useState(false);

  useEffect(() => {
    if (!location.pathname.startsWith("/equipment") || !location.search.includes("tab=armory")) {
      navigate("/equipment?tab=armory");
    } else {
      setHasSeen(true);
    }
  }, [location.pathname, location.search, navigate]);

  // Poll to detect when user equips gear (check if step changed)
  useEffect(() => {
    const check = async () => {
      try {
        const { data } = await api.get("/onboarding");
        if (data.step !== "equip_gear") onEquipped();
      } catch {}
    };
    const id = setInterval(check, 3000);
    return () => clearInterval(id);
  }, [onEquipped]);

  // Also let user manually advance once they've equipped
  const markDone = async () => {
    try {
      await api.post("/onboarding/complete-equip");
      onEquipped();
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  if (!hasSeen) return null;

  return (
    <div data-testid="equip-prompt" className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 max-w-md w-full mx-4">
      <div className="border border-amber-600/50 bg-zinc-950/95 backdrop-blur-sm rounded-sm p-4 shadow-[0_0_24px_rgba(217,119,6,0.15)]">
        <div className="flex items-start gap-3">
          <Shield size={20} className="text-amber-400 shrink-0 mt-0.5" />
          <div className="flex-1">
            <h4 className="font-cinzel text-sm uppercase tracking-wide text-amber-300 mb-1">Equip Your Gladiator</h4>
            <p className="text-xs text-zinc-400 leading-relaxed">
              Select <span className="text-zinc-200 font-medium">{gladiator?.name || "your gladiator"}</span> and equip the gear you just forged. Choose a slot and select your equipment to assign it.
            </p>
            <button
              data-testid="equip-done-btn"
              onClick={markDone}
              className="mt-2.5 text-[10px] uppercase tracking-widest text-amber-400 hover:text-amber-300 transition-colors"
            >
              I&apos;m ready to fight &rarr;
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


// ─── Step 3: First Battle ───────────────────────────────────────────────────

function FirstBattleStep({ gladiator, onComplete }) {
  const [battleId, setBattleId] = useState(null);
  const [reward, setReward] = useState(null);
  const [fighting, setFighting] = useState(false);
  const [replayDone, setReplayDone] = useState(false);

  const startBattle = async () => {
    setFighting(true);
    try {
      const { data } = await api.post("/onboarding/complete-battle");
      setBattleId(data.battle_id);
      setReward(data.reward_item);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setFighting(false);
    }
  };

  // Show reward modal after replay closes
  if (replayDone && reward) {
    return (
      <Modal open title="First Blood!" wide>
        <div className="text-center py-4">
          <h3 className="font-cinzel text-xl uppercase tracking-wide text-amber-300 mb-2">
            Battle Complete
          </h3>
          <p className="text-sm text-zinc-400 mb-6">
            Your gladiator has proven themselves in the arena. A gift awaits in your armory.
          </p>
          <div className="border border-amber-600/30 bg-amber-950/20 rounded-sm p-4 inline-flex items-center gap-3 mb-6">
            <Shield size={24} className="text-emerald-400" />
            <div className="text-left">
              <div className="text-xs uppercase tracking-widest text-zinc-500">Reward</div>
              <div className="text-sm text-zinc-200 font-medium">{reward.name}</div>
              <div className="text-[10px] text-emerald-400 capitalize">{reward.rarity} {reward.kind}</div>
            </div>
          </div>
          <div>
            <button
              data-testid="onboarding-finish"
              onClick={onComplete}
              className="px-6 py-2.5 text-sm font-cinzel uppercase tracking-widest bg-amber-500 text-zinc-950 hover:bg-amber-400 transition-colors rounded-sm"
            >
              Enter the Arena
            </button>
          </div>
        </div>
      </Modal>
    );
  }

  // Full battle replay
  if (battleId) {
    return (
      <BattleReplay
        battleId={battleId}
        open
        onClose={() => setReplayDone(true)}
        title="Prolusio — First Blood"
        exhibition
      />
    );
  }

  return (
    <Modal open title="Your First Battle Awaits">
      <div className="text-center py-4">
        <h3 className="font-cinzel text-lg uppercase tracking-wide text-amber-300 mb-2">
          Prolusio: Tiro Novellus
        </h3>
        <p className="text-sm text-zinc-400 mb-6">
          A raw recruit awaits in the practice arena. Send <span className="text-zinc-200 font-medium">{gladiator?.name}</span> to prove their worth.
        </p>
        <button
          data-testid="onboarding-fight"
          onClick={startBattle}
          disabled={fighting}
          className="px-6 py-2.5 text-sm font-cinzel uppercase tracking-widest bg-amber-500 text-zinc-950 hover:bg-amber-400 disabled:opacity-50 transition-colors rounded-sm mx-auto"
        >
          {fighting ? "Fighting..." : "Fight!"}
        </button>
      </div>
    </Modal>
  );
}


// ─── Main Onboarding Orchestrator ───────────────────────────────────────────

export default function OnboardingFlow() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(null);
  const [data, setData] = useState(null);
  const [traitGlad, setTraitGlad] = useState(null);

  useEffect(() => {
    if (!user) return;
    api.get("/onboarding")
      .then((r) => {
        setStep(r.data.step);
        setData(r.data);
      })
      .catch(() => setStep("complete"));
  }, [user]);

  // Step 1 complete → open trait selection, then advance to step 2
  const handlePicked = (glad) => {
    setTraitGlad(glad);
  };

  const handleTraitsAssigned = async (traits) => {
    if (!traitGlad) return;
    try {
      await api.post(`/gladiators/${traitGlad.id}/assign-traits`, traits);
      toast.success(`Traits assigned to ${traitGlad.name}!`);
      setTraitGlad(null);
      // Refresh onboarding state
      const { data: d } = await api.get("/onboarding");
      setStep(d.step);
      setData(d);
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const handleForged = () => {
    api.get("/onboarding").then((r) => {
      setStep(r.data.step);
      setData(r.data);
    });
  };

  const handleEquipped = () => {
    api.get("/onboarding").then((r) => {
      setStep(r.data.step);
      setData(r.data);
    });
  };

  const handleBattleComplete = () => {
    setStep("complete");
    navigate("/");
    toast.success("Welcome to the arena, Lanista!");
  };

  // Don't show anything for completed onboarding or during loading
  if (!step || step === "complete") return null;

  // Trait selection modal (between step 1 and 2)
  if (traitGlad) {
    return (
      <TraitSelectionModal
        gladiator={traitGlad}
        onConfirm={handleTraitsAssigned}
        onClose={() => {}}
        required
      />
    );
  }

  if (step === "pick_gladiator" && data?.candidates) {
    return <PickGladiatorStep candidates={data.candidates} onPicked={handlePicked} />;
  }

  if (step === "forge_equipment") {
    return <ForgePromptStep gladiator={data?.gladiator} onForged={handleForged} />;
  }

  if (step === "equip_gear") {
    return <EquipGearStep gladiator={data?.gladiator} onEquipped={handleEquipped} />;
  }

  if (step === "first_battle" && data?.gladiator) {
    return <FirstBattleStep gladiator={data.gladiator} onComplete={handleBattleComplete} />;
  }

  return null;
}
